import * as React from 'react';
import { useEffect, useState } from 'react'
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { visuallyHidden } from '@mui/utils'; 
import Divider from '@mui/material/Divider';
import styled from '@emotion/styled';
import Select from "react-select";
import { useSelector, useDispatch } from 'react-redux';
import ArrowBackIosNewSharpIcon from '@mui/icons-material/ArrowBackIosNewSharp';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import * as routes from "../../../Router/RoutesURL";
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    stableSort,
    getComparator,
    rowOptions,
    addSuffix
} from '../../../utils/Helper'
import { viewAssessments } from '../../../redux/actions/MasterDataAction/AssessmentAction/AssessmentAction';
import {
    FormGroup,
} from "reactstrap";
import './Assessment.scss';
import { CircularProgress } from '@mui/material';
import { tabKeys } from '../../../Config/Constant';

const MainHeading = styled(Typography)(({ theme }) => ({
    color: theme.palette.primary.main,
}))

const headCells = [
    {
        id: 'section_name',
        numeric: false,
        disablePadding: true,
        label: 'SECTIONS',
    },
    {
        id: 'questions_count',
        numeric: true,
        disablePadding: false,
        label: 'QUESTION COUNT',
    },
    {
        id: 'position',
        numeric: true,
        disablePadding: false,
        label: 'POSITION',
    },
    {
        id: 'created_on',
        numeric: true,
        disablePadding: false,
        label: 'CREATED ON',
    },
    {
        id: 'action',
        numeric: true,
        disablePadding: false,
        label: 'ACTION',
        disableSorting: true
    }
];

const EnhancedTableHead = (props) => {
    const { order, orderBy, onRequestSort } = props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align='left'
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id && !headCell.disableSorting}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                            hideSortIcon={headCell.disableSorting}
                            disabled={headCell.disableSorting}
                            >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

const EnhancedTableToolbar = (props) => {
    const { numSelected, activeTab, setShowLoader} = props;
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchText, setSearchText] = useState(searchParams.get('search_text') || '');
    const dispatch = useDispatch();

    useEffect(() => {
        if (activeTab == tabKeys.masterData.ASSESSMENT) {
            setSearchText(searchParams.get('search_text') || '');
        }
    }, [activeTab, searchParams]);

    const handleSearchText = (event) => {
        setShowLoader(true)
        const newSearchText = event.target.value;
        setSearchText(newSearchText);

        const newParams = new URLSearchParams(searchParams);
        if (newSearchText) {
            newParams.set('search_text', newSearchText);
        } else {
            newParams.delete('search_text');
        }
        newParams.set('pageNo', 1);
        setSearchParams(newParams, { replace: true });
    };

    return (

        <div>
            <Toolbar sx={{
                pl: { sm: 2 },
                pt: { sm: 2 },
                pb: { sm: 2 },
                pr: { xs: 1, sm: 1 }
            }}
            >
                <div className='col-2 col-sm-2 col-md-2 col-lg-2 col-xl-2 col-xxl-4'>
                    <MainHeading
                        sx={{ flex: '1 1 100%', }}
                        variant="h6"
                        id="tableTitle"
                        component="div"
                    >Section List
                    </MainHeading>
                </div>
                <div className='col-2 col-sm-2 col-md-2 col-lg-2 col-xl-10 col-xxl-8 LevelDiv'>
                    <div className="mr-sm-2 searchDiv class-search ">
                        <input
                            className="form-control mr-sm-2 class-search"
                            type="search"
                            placeholder=" Search Keyword"
                            aria-label="Search"
                            value={searchText} 
                            onChange={handleSearchText}
                        ></input>
                    </div>
                </div>
            </Toolbar>
            <Divider />
        </div>
    );
}

EnhancedTableToolbar.propTypes = {
    numSelected: PropTypes.number.isRequired,
};

const AssessmentTable = (props) => {
    const { activeTab, permission } = props;
    const [searchParams, setSearchParams] = useSearchParams();
    const [order, setOrder] = useState(searchParams.get('order_by_type') || '');
    const [orderBy, setOrderBy] = useState(searchParams.get('order_by') || '');
    const [selected, setSelected] = useState([]);
    const [rowsPerPage, setRowsPerPage] = useState(parseInt(searchParams.get('limit')) || 10);  
    const [viewAssessmentMainData, setViewAssessmentMainData] = useState('');
    const [showLoader, setShowLoader] = useState(false);
    const viewAssesData = useSelector(state => state?.viewAssessments);
    const viewAssessmentsDataRes = useSelector(state => state?.viewAssessments?.viewAssessments);
    const AssessmentsData = viewAssessmentsDataRes?.data?.program_data

    const dispatch = useDispatch();
    const navigate = useNavigate()

    useEffect(() => {
        if (activeTab == tabKeys.masterData.ASSESSMENT) {
            const orderDir = searchParams.get('order_by_type') || '';
            const orderByCol = searchParams.get('order_by') || '';
            const sendRequest = {
                "pageNo": searchParams.get('pageNo') || 1,
                "pageSize": searchParams.get('limit') || 10,
                "search_text": searchParams.get('search_text') || '',
                "orderBy": orderByCol,
                "order": orderDir
            };
            dispatch(viewAssessments(sendRequest));
        }else{
            setOrder('');
            setOrderBy('');
            setRowsPerPage(10)
        }
    }, [activeTab, searchParams]);

    useEffect(()=>{
        if(!viewAssesData.loading)
            setShowLoader(false)
        if(!viewAssesData.loading && !viewAssessmentsDataRes?.data?.program_data && !viewAssesData.error){
            setShowLoader(true)
        }
    }, [viewAssesData.loading])

    useEffect(() => {
        if (viewAssesData.error !== "Ananda self assessment data does not exists") {
            setViewAssessmentMainData(viewAssessmentsDataRes && viewAssessmentsDataRes?.data?.program_data);

        } else {
            setViewAssessmentMainData("");
        }

    }, [viewAssessmentsDataRes, AssessmentsData, viewAssesData])

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';
        setOrder(newOrder);
        setOrderBy(property);
        const newParams = new URLSearchParams(searchParams);
        newParams.set('order_by_type', newOrder);
        newParams.set('order_by', property);
        setSearchParams(newParams, { replace: true });
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelected = viewAssessmentsDataRes?.data?.program_data.map((n) => n.name);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };
    
    const handleClick = (event, name) => {
        const selectedIndex = selected.indexOf(name);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, name);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }

        setSelected(newSelected);
    };

    const handleChangePageNext = (newPage) => {
        const nextPage = viewAssessmentsDataRes?.data?.current_page_no + 1;
        const newParams = new URLSearchParams(searchParams);
        newParams.set('pageNo', nextPage);
        setSearchParams(newParams, { replace: true });
        setShowLoader(true)

        const sendRequest = {
            "pageNo": nextPage,
            "pageSize": rowsPerPage,
            "search_text": searchParams.get('search_text') || '',
        };
        dispatch(viewAssessments(sendRequest));
    };

    const handleChangePagePrev = (newPage) => {
        const prevPage = viewAssessmentsDataRes?.data?.current_page_no - 1;
        const newParams = new URLSearchParams(searchParams);
        newParams.set('pageNo', prevPage);
        setSearchParams(newParams, { replace: true });
        setShowLoader(true)

        const sendRequest = {
            "pageNo": prevPage,
            "pageSize": rowsPerPage,
            "search_text": searchParams.get('search_text') || '',
        };
        dispatch(viewAssessments(sendRequest));
    };
    const handleChangeRowsPerPage = (event) => {
        setShowLoader(true)
        const newParams = new URLSearchParams(searchParams);
        newParams.set('limit', event.value);
        newParams.set('pageNo', 1);
        setSearchParams(newParams, { replace: true });
        setRowsPerPage(event.value);
    };

    const handleChange = (selectedAssessmentId, selectSectionName, assessmentStatus) => {
        navigate(routes.VIEW_ASSESSMENT, { state: { selectedAssessmentId, selectSectionName, assessmentStatus, permission } });
    };

    return (

      <Box sx={{ width: '100%' }} className="AssessmentBox h-100">
      <Paper className='d-flex flex-column' sx={{ width: '100%', mb: 1, borderRadius: 2, height: 'calc(100% - 57px)' }}>
                <EnhancedTableToolbar numSelected={selected.length}
                    showLoader={showLoader}
                    setShowLoader={setShowLoader}
                    activeTab={activeTab} />
                {
                    showLoader ?
                    <div className='w-100 text-center py-5'>
                        <CircularProgress sx={{'color': '#74613C'}}/>
                    </div>
                    :
                    AssessmentsData && viewAssessmentMainData ? <TableContainer sx={{ pt: 0, pr: 3, pb: 3, pl: 3, height: '100%' }}>
                    <Table
                        sx={{ minWidth: 750 }}
                        aria-labelledby="tableTitle" 
                        size='small'
                        stickyHeader
                    >
                        <EnhancedTableHead
                            numSelected={selected.length}
                            order={order}
                            orderBy={orderBy}
                            onSelectAllClick={handleSelectAllClick}
                            onRequestSort={handleRequestSort}
                            rowCount={viewAssessmentsDataRes?.data?.program_data.length}

                        />
                        <TableBody>
                            { 
                            viewAssessmentsDataRes && stableSort(viewAssessmentMainData, getComparator(order, orderBy))
                                .map((row, index) => {

                                    const labelId = `enhanced-table-checkbox-${index}`;
                                    return (
                                        <TableRow hover onClick={(event) => handleClick(event, row.name)} role="checkbox"

                                            tabIndex={-1}
                                            key={row.section_name}

                                        >
                                            <TableCell component="th" id={labelId} scope="row" padding="none">{row.section_name}</TableCell>
                                            <TableCell align="left">{row.questions_count}</TableCell>
                                            <TableCell align="left">{addSuffix(row.position)}</TableCell>
                                            <TableCell align="left">{row.created_on}</TableCell>
                                            <TableCell align="left" className='View' onClick={() => { handleChange(row.section_id, row.section_name, row.status) }}><i className="fas fa-eye mr-1"></i> View</TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
                : <div className='text-center w-100 text-muted mt-3'>No Records </div>
                }
            </Paper>
      
            {
                viewAssessmentMainData
                &&
                <div className='paginationBlock'>
                    <div className='textData'><p>Items per page: </p></div>
                    <FormGroup className="FormGroup has-float-label rowsSelect class-menu-dropdown ">
                        <Select
                            placeholder={rowsPerPage}
                            className="react-select"
                            classNamePrefix="react-select"
                            value={rowsPerPage}
                            options={rowOptions}
                            isSearchable={false}
                            onChange={handleChangeRowsPerPage}
                            theme={(theme) => ({
                                ...theme,
                                colors: {
                                    ...theme.colors,
                                    primary25: '#F5F5F5',
                                    primary: '#74613C',
                                },
                            })}
                            menuPlacement="top"
                            menuPosition="fixed"
                            styles={{
                                menu: (base) => ({
                                    ...base,
                                    top: 'auto',
                                    bottom: 0,
                                }),
                            }}
                        />
                    </FormGroup>
                    {viewAssessmentsDataRes?.data && <p className='endText'>   {viewAssessmentsDataRes?.data?.start} - {viewAssessmentsDataRes?.data?.end} of {viewAssessmentsDataRes?.data?.total}</p>}
                    <p>                       
                        <button className={`btnPrev ${viewAssessmentsDataRes?.data?.current_page_no === 1 ? "opacityBtns" : ''}`} disabled={viewAssessmentsDataRes?.data?.current_page_no === 1} onClick={() => { handleChangePagePrev() }}>
                            <ArrowBackIosNewSharpIcon className='iconBtn' />
                        </button> 
                        <button className={`btnPrev ${viewAssessmentsDataRes?.data?.current_page_no === viewAssessmentsDataRes?.data?.total_pages ? "opacityBtns" : ''}`} disabled={viewAssessmentsDataRes?.data?.current_page_no === viewAssessmentsDataRes?.data?.total_pages} onClick={() => { handleChangePageNext() }}>
                            <ArrowForwardIosSharpIcon className='iconBtn' />
                        </button></p>
                </div>
            }
        </Box>
    );
}
export default AssessmentTable;